import React, {useCallback} from 'react';
import {useFormikContext} from 'formik';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {DrawerFaceHeight} from 'components/customer/Product/Drawer/DrawerFaceHeight';
import {SelectedHardware} from 'components/customer/Product/Drawer/SelectedHardware';
import {useFollowsCabinet} from 'components/customer/Product/Drawer/helpers/useFollowsCabinet';
import {Icon} from 'shared/helpers';
import {
    Badge,
    Heading,
    Panel,
    PanelBlock,
    ResetButton,
} from 'components/customer/Product/Drawer/layoutStyles';

interface SelectedDrawerProps {
    drawerParams: DrawerParameters;
    drawerAmount: number;
    selectedIndex: number;
}

// every drawer stays mounted so its runner lookup keeps running; only the
// selected one is shown
export const SelectedDrawer = ({
    drawerParams,
    drawerAmount,
    selectedIndex,
}: SelectedDrawerProps) => {
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const usesDefaults = useFollowsCabinet(drawerParams);

    const autoHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const index = Number(event.currentTarget.dataset.index);

            void setTouched({drawers: true});
            void setFieldValue(
                `drawers[${index}].${drawerParams.drawerStyleFieldName}`,
                1
            );
        },
        [setFieldValue, setTouched, drawerParams.drawerStyleFieldName]
    );

    return (
        <Panel data-cy="selected-drawer">
            {Array.from(Array(drawerAmount).keys()).map((index) => {
                const drawer = values.drawers?.[Number(index)];
                const isAuto =
                    drawer?.[
                        drawerParams.drawerStyleFieldName as keyof typeof drawer
                    ] == 1;

                return (
                    <PanelBlock
                        key={index}
                        hidden={index !== selectedIndex}
                        data-cy={`selected-drawer-${index}`}>
                        <Heading data-cy={`drawer-heading-${index}`}>
                            <Badge>{index + 1}</Badge>
                            <span>Drawer {index + 1}</span>
                        </Heading>

                        <DrawerFaceHeight
                            runnerIndex={index}
                            fieldName={drawerParams.drawerFaceHeightFieldName}
                            drawerStyleFieldName={
                                drawerParams.drawerStyleFieldName
                            }
                            setsFixedOnChange
                            trailing={
                                <ResetButton
                                    variant="link"
                                    className="button-blue btn-sm"
                                    data-index={index}
                                    data-cy={`drawer-auto-${index}`}
                                    onClick={autoHandler}
                                    disabled={isAuto}>
                                    <Icon
                                        iconName="Reverse-Refresh.svg"
                                        color="white"
                                    />
                                    <span>Auto</span>
                                </ResetButton>
                            }
                        />

                        <SelectedHardware
                            drawerParams={drawerParams}
                            index={index}
                            usesDefaults={usesDefaults(drawer)}
                        />
                    </PanelBlock>
                );
            })}
        </Panel>
    );
};
